import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Briefcase, Building2, UserCheck, ClipboardList, MapPin, TrendingUp } from 'lucide-react';
import { getUser } from '@/utils/auth';
import api from '@/utils/api';

const ExecutiveDashboardHome = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState({
    total_jobs: 0,
    active_jobs: 0,
    total_employers: 0,
    total_applications: 0,
    allocated_districts: 0
  });
  const [loading, setLoading] = useState(true);
  const [allocatedDistricts, setAllocatedDistricts] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchExecutiveDetails();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get(`/executive/dashboard-stats?executive_id=${user.id}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExecutiveDetails = async () => {
    try {
      const response = await api.get(`/admin/executives/${user.id}`);
      setAllocatedDistricts(response.data.allocated_districts || []);
    } catch (error) {
      console.error('Failed to fetch executive details', error);
    }
  };

  const statCards = [
    {
      title: 'Total Jobs',
      value: stats.total_jobs,
      icon: Briefcase,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-50',
      path: '/admin/executive-jobs'
    },
    {
      title: 'Active Jobs',
      value: stats.active_jobs,
      icon: TrendingUp,
      color: 'bg-green-500',
      lightColor: 'bg-green-50',
      path: '/admin/executive-jobs'
    },
    {
      title: 'Employers',
      value: stats.total_employers,
      icon: Building2,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-50',
      path: '/admin/executive-employers'
    }
  ];

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
          Executive Dashboard
        </h1>
        <p className="text-slate-600">Welcome back, {user.name || user.mobile}!</p>
      </div>

      {/* Allocated Districts Section */}
      <Card className="mb-8 p-6 border-slate-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Your Allocated Districts</h2>
            <p className="text-sm text-slate-500">You can manage jobs and employers in these districts</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {allocatedDistricts.length === 0 ? (
            <p className="text-slate-500 text-sm">No districts allocated yet. Please contact admin.</p>
          ) : (
            allocatedDistricts.map((d, index) => (
              <Badge key={index} variant="outline" className="px-3 py-1.5">
                <MapPin className="w-3 h-3 mr-1" />
                {d.state} / {d.district}
              </Badge>
            ))
          )}
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Card 
            key={index}
            className="p-6 border-slate-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(stat.path)}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">{stat.title}</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 ${stat.lightColor} rounded-lg flex items-center justify-center`}>
                <stat.icon className={`w-6 h-6 ${stat.color.replace('bg-', 'text-')}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card className="p-6 border-slate-200">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/admin/executive-jobs')}
            className="p-4 bg-slate-50 hover:bg-slate-100 rounded-lg text-left transition-colors"
          >
            <Briefcase className="w-6 h-6 text-blue-600 mb-2" />
            <p className="font-medium text-slate-900">Manage Jobs</p>
            <p className="text-sm text-slate-500">Approve, activate, or deactivate jobs</p>
          </button>
          
          <button
            onClick={() => navigate('/admin/executive-employers')}
            className="p-4 bg-slate-50 hover:bg-slate-100 rounded-lg text-left transition-colors"
          >
            <Building2 className="w-6 h-6 text-purple-600 mb-2" />
            <p className="font-medium text-slate-900">View Employers</p>
            <p className="text-sm text-slate-500">Manage employers in your districts</p>
          </button>
          
          <button
            onClick={() => navigate('/admin/executive-applications')}
            className="p-4 bg-slate-50 hover:bg-slate-100 rounded-lg text-left transition-colors"
          >
            <ClipboardList className="w-6 h-6 text-orange-600 mb-2" />
            <p className="font-medium text-slate-900">View Applications</p>
            <p className="text-sm text-slate-500">Review job applications</p>
          </button>
        </div>
      </Card>

      {/* Info Notice */}
      <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800">
          <strong>Note:</strong> As an executive, you can only view and manage data within your allocated districts. 
          All your actions (activate/deactivate/edit) are logged for audit purposes. Contact the admin if you need 
          access to additional districts.
        </p>
      </div>
    </div>
  );
};

export default ExecutiveDashboardHome;
